import { components } from "@/api-schema";
import {
  CompanyType,
  PathsV1CompanySearchPostParametersQueryExtend,
  PathsV1CompanySearchPostParametersQuerySelect,
  PathsV1OccupationSearchPostParametersQuerySelect,
} from "@/api-schema-enums";
import { useCompanyGroupSearchQuery } from "@/common/api/company-group/use-company-group-search.query";
import { useCompanySearchQuery } from "@/common/api/company/use-company-search.query";
import { useCompanyUpdateMutation } from "@/common/api/company/use-company-update.mutation";
import { useOccupationSearchQuery } from "@/common/api/occupation/use-occupation-search.query";
import { fetchFileFromUrl } from "@/common/helpers/file";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { FileUploader } from "@/components/file-uploader/file-uploader";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormCardActions } from "@/components/ui/form-card";
import { Input } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { MultiSelect } from "@/components/ui/multi-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { SystemInfoFields, SystemInfoIdentifierType } from "@/components/system-info-fields/system-info-fields";
import { CompanyIdentifierFormCard } from "@/modules/masterdata/company/_components/company-identifier-form-card";
import {
  companyIdentifierFormDefaultValues,
  companyIdentifierFormSchema,
  getDuplicateCompanyIdentifierKeys,
  getCompanySapIdentifier,
  normalizeCompanySapIdentifier,
  toCompanyIdentifierDtos,
  toCompanyIdentifierFormValues,
} from "@/modules/masterdata/company/company-identifiers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { z } from "zod";
import { renderCompanyType } from "../../_helpers/type-renderer";

type Company = components["schemas"]["Company"];

const COMMERCIAL_REGISTER_NUMBER_PATTERN = /^(HR\s?-?\s?[AB]|GnR|PR|VR)\s?\d+[A-Z]?$/i;
const VAT_ID_PATTERN = /^[A-Z]{2}[A-Z0-9]{8,12}$/;

export const CompanyUpdateFormSchema = (t: (key: string) => string) =>
  z.object({
    companyIdentifiers: companyIdentifierFormSchema,
    name: z.string().min(1, t("masterdata:NameRequired")),
    type: z.enum(CompanyType),
    logo: z.instanceof(File).optional().nullable(),
    commercialRegisterCourt: z.string().trim().optional(),
    commercialRegisterNumber: z
      .string()
      .trim()
      .optional()
      .refine((value) => !value || COMMERCIAL_REGISTER_NUMBER_PATTERN.test(value), {
        message: t("masterdata:CompanyCommercialRegisterNumberInvalid"),
      }),
    vatId: z
      .string()
      .trim()
      .optional()
      .transform((value) => value?.replace(/\s+/g, "").toUpperCase())
      .refine((value) => !value || VAT_ID_PATTERN.test(value), {
        message: t("masterdata:CompanyVatIdInvalid"),
      }),
    managingDirectorOccupations: z.array(z.string()),
    companyGroup: z.string().min(1, t("masterdata:CompanyGroupIsRequired")),
  });

export type CompanyUpdateFormSchemaType = z.infer<ReturnType<typeof CompanyUpdateFormSchema>>;

interface CompanyUpdateFormFieldsProps {
  company?: Company;
  uuid: string;
}

function getCompanyFormValues(company?: Company): CompanyUpdateFormSchemaType {
  return {
    companyIdentifiers: toCompanyIdentifierFormValues(company?.companyIdentifiers),
    name: company?.name || "",
    type: company?.type || undefined,
    logo: undefined,
    commercialRegisterCourt: company?.commercialRegisterCourt || "",
    commercialRegisterNumber: company?.commercialRegisterNumber || "",
    vatId: company?.vatId || "",
    managingDirectorOccupations: company?.managingDirectorOccupations ?? [],
    companyGroup: company?.companyGroup?.uuid ?? "",
  };
}

export function CompanyUpdateFormFields({ company, uuid }: CompanyUpdateFormFieldsProps) {
  const { t } = useTranslation(["masterdata", "common"]);
  const [_, navigate] = useLocation();
  const { mutateAsync, isPending } = useCompanyUpdateMutation();

  // The parent (see update.view.tsx) only mounts this component once `company` has finished
  // loading and keys it by `company.uuid`, so `company` is guaranteed to be defined here on the
  // very first render. Seeding `defaultValues` directly from it (instead of resetting after
  // mount) avoids a render where the `type` Select is still "empty", which is what previously
  // forced users to re-pick the type on every edit.
  const form = useForm<CompanyUpdateFormSchemaType>({
    resolver: zodResolver(CompanyUpdateFormSchema(t)),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: getCompanyFormValues(company),
  });

  const persistedCompanyIdentifiers = toCompanyIdentifierFormValues(company?.companyIdentifiers);
  const isSapIdentifierLocked = normalizeCompanySapIdentifier(getCompanySapIdentifier(company ?? {}) ?? "") === "10";

  const [watchedName, watchedCompanyIdentifiers] = useWatch({
    control: form.control,
    name: ["name", "companyIdentifiers"],
  });

  const { data: existingCompanies, isLoading: isExistingCompaniesLoading } = useCompanySearchQuery({
    options: {
      body: {},
      parameters: {
        pageSize: -1,
        extend: [PathsV1CompanySearchPostParametersQueryExtend.CompanyIdentifiers],
        select: [
          PathsV1CompanySearchPostParametersQuerySelect.Uuid,
          PathsV1CompanySearchPostParametersQuerySelect.Name,
          PathsV1CompanySearchPostParametersQuerySelect.CompanyIdentifiers,
        ],
      },
    },
  });
  const { data: occupations, isLoading: isOccupationsLoading } = useOccupationSearchQuery({
    options: {
      body: {},
      parameters: {
        pageSize: -1,
        select: [
          PathsV1OccupationSearchPostParametersQuerySelect.Uuid,
          PathsV1OccupationSearchPostParametersQuerySelect.Name,
        ],
      },
    },
  });

  const occupationOptions = useMemo(() => {
    return (
      occupations?.data?.map((occupation) => ({
        value: occupation.uuid,
        label: occupation.name,
        searchText: occupation.name,
      })) ?? []
    );
  }, [occupations]);

  const { data: companyGroupsData } = useCompanyGroupSearchQuery({
    options: {
      body: {},
      parameters: {
        pageSize: -1,
      },
    },
  });

  const onSubmit = async (values: CompanyUpdateFormSchemaType) => {
    if (!uuid) return;

    try {
      await toastMutationStatus({
        mutationFn: () =>
          mutateAsync({
            uuid,
            name: values.name.trim(),
            type: values.type,
            companyIdentifiers: toCompanyIdentifierDtos({
              ...values.companyIdentifiers,
              v1: persistedCompanyIdentifiers.v1,
              sap: isSapIdentifierLocked ? persistedCompanyIdentifiers.sap : values.companyIdentifiers.sap,
            }),
            commercialRegisterCourt: values.commercialRegisterCourt?.trim() || null,
            commercialRegisterNumber: values.commercialRegisterNumber?.trim() || null,
            vatId: values.vatId?.trim() || null,
            managingDirectorOccupations: values.managingDirectorOccupations,
            logo: form.getFieldState("logo").isDirty ? values.logo : undefined,
            companyGroup: values.companyGroup,
          }),
        successMessage: t("masterdata:CompanySuccessfullyUpdated"),
        errorMessage: t("masterdata:ErrorUpdatingCompany"),
        customErrors: { 409: t("masterdata:CompanyIdentifierConflict") },
      });

      navigate("~/masterdata/company");
    } catch (error) {
      console.error("Error updating company:", error);
    }
  };

  useEffect(() => {
    if (!company?.logoUrl) {
      return;
    }

    let isMounted = true;

    void fetchFileFromUrl(company.logoUrl, company.logoPath ?? undefined)
      .then((file) => {
        if (isMounted) {
          form.setValue("logo", file, { shouldDirty: false, shouldValidate: false });
        }
      })
      .catch((error: unknown) => {
        console.error("Error loading company logo:", error);
      });

    return () => {
      isMounted = false;
    };
  }, [company?.logoPath, company?.logoUrl, form]);

  const duplicateValidation = useMemo(() => {
    const errors: string[] = [];

    if (existingCompanies?.data && Array.isArray(existingCompanies.data) && watchedName?.trim()) {
      const duplicateName = existingCompanies.data.find(
        (company: Company) => company.name?.toLowerCase() === watchedName.toLowerCase().trim() && company.uuid !== uuid,
      );
      if (duplicateName) {
        errors.push(t("masterdata:CompanyNameAlreadyExists", { name: watchedName }));
      }
    }

    const duplicateKeys = getDuplicateCompanyIdentifierKeys(
      existingCompanies?.data,
      watchedCompanyIdentifiers ?? companyIdentifierFormDefaultValues,
      uuid,
    );
    if (duplicateKeys.size > 0) errors.push(t("masterdata:CompanyIdentifierConflict"));

    return { duplicateKeys, hasErrors: errors.length > 0, errors };
  }, [existingCompanies?.data, t, uuid, watchedCompanyIdentifiers, watchedName]);

  const handleSubmitWithValidation = (values: CompanyUpdateFormSchemaType) => {
    if (duplicateValidation.hasErrors) {
      duplicateValidation.errors.forEach((error) => {
        toast.error(t("masterdata:ValidationError"), {
          description: error,
          duration: 5000,
        });
      });
      return;
    }
    onSubmit(values);
  };

  const isLoading = !company || isExistingCompaniesLoading || isOccupationsLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-6">
          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>

          <div className="flex justify-end space-x-4">
            <Skeleton className="h-10 w-24 rounded-md" />
            <Skeleton className="h-10 w-24 rounded-md" />
          </div>
        </div>
      </div>
    );
  }

  const isSubmitDisabled = isPending || duplicateValidation.hasErrors || !form.formState.isValid;

  return (
    <Form {...form}>
      <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmitWithValidation)}>
        <Card>
          <CardContent className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("common:Name")} *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder={t("masterdata:EnterCompanyName")}
                        {...field}
                        error={duplicateValidation.errors.some((e) => e.includes(t("common:Name")))}
                        disabled={isExistingCompaniesLoading}
                      />
                      {isExistingCompaniesLoading && (
                        <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                          <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                  {isExistingCompaniesLoading ? (
                    <p className="text-muted-foreground mt-1 text-sm">
                      {t("masterdata:CheckingDuplicateCompanyNames")}
                    </p>
                  ) : (
                    duplicateValidation.errors.some((e) => e.includes(t("common:Name"))) && (
                      <p className="text-error mt-1 text-sm">{t("masterdata:CompanyNameDuplicateError")}</p>
                    )
                  )}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("masterdata:Type")} *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        {/* Radix only shows an item's label in the closed trigger once that
                            SelectItem has actually rendered/matched (i.e. after the dropdown was
                            opened at least once). For a value hydrated programmatically from the
                            fetched company, that never happens, so we render the label explicitly
                            here to avoid a blank/placeholder-looking trigger on load. */}
                        <SelectValue placeholder={t("masterdata:SelectType")}>
                          {field.value ? renderCompanyType(field.value) : undefined}
                        </SelectValue>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(CompanyType).map((type) => (
                        <SelectItem key={type} value={type}>
                          {renderCompanyType(type)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="companyGroup"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("masterdata:CompanyGroup")} *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("masterdata:SelectCompanyGroup")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {(companyGroupsData?.data ?? []).map((companyGroup) => (
                        <SelectItem key={companyGroup.uuid} value={companyGroup.uuid}>
                          {companyGroup.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="managingDirectorOccupations"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("masterdata:CompanyManagingDirectorOccupation")}</FormLabel>
                  <MultiSelect
                    options={occupationOptions}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder={t("masterdata:SelectManagingDirectorOccupation")}
                    disabled={isOccupationsLoading}
                    maxCount="auto"
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="commercialRegisterCourt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("masterdata:CompanyCommercialRegisterCourt")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("masterdata:CompanyCommercialRegisterCourtPlaceholder")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="commercialRegisterNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("masterdata:CompanyCommercialRegisterNumber")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("masterdata:CompanyCommercialRegisterNumberPlaceholder")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="vatId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("masterdata:CompanyVatId")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("masterdata:CompanyVatIdPlaceholder")}
                      {...field}
                      onChange={(event) => field.onChange(event.target.value.toUpperCase())}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="logo"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <FileUploader
                      label={t("common:Logo")}
                      maxFiles={1}
                      value={field.value}
                      onFileSelect={(file) => field.onChange(file)}
                      onFileRemove={() => field.onChange(null)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SystemInfoFields
              identifiers={
                company?.technicalId
                  ? [
                      {
                        value: company.technicalId,
                        label: { kind: "known", type: SystemInfoIdentifierType.TechnicalId },
                      },
                    ]
                  : undefined
              }
              uuid={company?.uuid}
              createdAt={company?.createdAt}
              updatedAt={company?.updatedAt}
            />
          </CardContent>
        </Card>

        <CompanyIdentifierFormCard
          duplicateKeys={duplicateValidation.duplicateKeys}
          isCheckingDuplicates={isExistingCompaniesLoading}
          isSapLocked={isSapIdentifierLocked}
        />

        <FormCardActions
          cancelLabel={t("common:Cancel")}
          onCancel={() => window.history.back()}
          isPending={isPending}
          submitDisabled={isSubmitDisabled}
          submitLabel={t("common:SaveChanges")}
        />
      </form>
    </Form>
  );
}
